import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Autocomplete,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { Add, Delete, Save, Send, ChevronLeft, ChevronRight, CalendarToday, CheckCircle, Cancel, ThumbUp, ThumbDown, Search as SearchIcon, ArrowBack } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Timesheet, TimesheetItem, Project, TimesheetStatus, Issue } from '../types';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';

interface TimesheetItemForm {
  id?: string;
  projectId: string;
  taskId: string;
  taskDescription: string;
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  description: string;
  billable: boolean;
}

const TimesheetForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [weekStartDate, setWeekStartDate] = useState<Date>(getMonday(new Date()));
  const [items, setItems] = useState<TimesheetItemForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [tyvraOptionsByRow, setTyvraOptionsByRow] = useState<Record<number, { label: string; value: string; id?: string; title?: string }[]>>({});
  const [optionsLoadingByRow, setOptionsLoadingByRow] = useState<Record<number, boolean>>({});
  const [tyvraInputByRow, setTyvraInputByRow] = useState<Record<number, string>>({});
  const tyvraSeqRef = useRef<Record<number, number>>({});
  const tyvraDebounceRef = useRef<Record<number, any>>({});
  const [assignedIssueSuggestions, setAssignedIssueSuggestions] = useState<Issue[]>([]);

  const parseHoursValue = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const normalized =
      typeof value === 'string' ? value.replace(',', '.').trim() : value;
    const parsed = parseFloat(normalized as string);
    if (Number.isNaN(parsed)) return 0;
    const bounded = Math.min(Math.max(parsed, 0), 24);
    return parseFloat(bounded.toFixed(2));
  };

  const fetchTyvraOptions = async (rowIndex: number, projectId: string, search?: string) => {
    if (!projectId) return;
    try {
      // sequence tracking to avoid out-of-order overwrites
      tyvraSeqRef.current[rowIndex] = (tyvraSeqRef.current[rowIndex] || 0) + 1;
      const seq = tyvraSeqRef.current[rowIndex];
      setOptionsLoadingByRow((prev) => ({ ...prev, [rowIndex]: true }));
      const res = await apiService.getIssues({ projectId, assigneeId: user?.id, search, limit: 25 } as any);
      const list: Issue[] = (res as any).data || [];
      const opts = list.map((iss) => {
        const id = (iss as any).timeTrackerTaskId || iss.issueKey || '';
        const title = iss.title || '';
        return {
          label: id && title ? `${id} - ${title}` : (id || title),
          value: id || title,
          id,
          title
        };
      });
      // only apply if latest
      if (seq === tyvraSeqRef.current[rowIndex]) {
        setTyvraOptionsByRow((prev) => ({ ...prev, [rowIndex]: opts }));
      }
    } catch (e) {
      setTyvraOptionsByRow((prev) => ({ ...prev, [rowIndex]: [] }));
    } finally {
      setOptionsLoadingByRow((prev) => ({ ...prev, [rowIndex]: false }));
    }
  };
  const [submitDialog, setSubmitDialog] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [approvalType, setApprovalType] = useState<'approve' | 'reject'>('approve');
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (!id) {
      loadTimesheetForWeek();
    }
  }, [weekStartDate]);

  useEffect(() => {
    // Load suggestions of issues assigned to current user
    const loadAssigned = async () => {
      try {
        if (!user?.id) return;
        const res = await apiService.getIssues({ assigneeId: user.id, limit: 30 } as any);
        const list: Issue[] = (res as any).data || [];
        setAssignedIssueSuggestions(list);
      } catch (e) {
        setAssignedIssueSuggestions([]);
      }
    };
    loadAssigned();
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading projects for timesheet form...');
      const projectsData = await apiService.getUserProjects();
      console.log('✅ Projects loaded:', projectsData?.length || 0, 'projects');
      setProjects(projectsData || []);

      if (id) {
        const timesheetData = await apiService.getTimesheetById(id);
        setTimesheet(timesheetData);
        setWeekStartDate(new Date(timesheetData.fromDate));
        setItems(
          timesheetData.items?.map(item => ({
            id: item.id,
            projectId: item.projectId,
            taskId: item.taskId || '',
            taskDescription: item.taskDescription,
            mondayHours: parseHoursValue(item.mondayHours),
            tuesdayHours: parseHoursValue(item.tuesdayHours),
            wednesdayHours: parseHoursValue(item.wednesdayHours),
            thursdayHours: parseHoursValue(item.thursdayHours),
            fridayHours: parseHoursValue(item.fridayHours),
            saturdayHours: parseHoursValue(item.saturdayHours),
            sundayHours: parseHoursValue(item.sundayHours),
            description: item.description || '',
            billable: item.billable,
          })) || []
        );
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTimesheetForWeek = async () => {
    if (!id) {
      try {
        const fromDate = weekStartDate.toISOString().split('T')[0];
        const toDate = new Date(weekStartDate);
        toDate.setDate(toDate.getDate() + 6);
        const toDateStr = toDate.toISOString().split('T')[0];
        
        const timesheetData = await apiService.getTimesheetByWeek(fromDate, toDateStr);
        if (timesheetData) {
          setTimesheet(timesheetData);
          setItems(
            timesheetData.items?.map(item => ({
              id: item.id,
              projectId: item.projectId,
              taskId: item.taskId || '',
              taskDescription: item.taskDescription,
              mondayHours: parseHoursValue(item.mondayHours),
              tuesdayHours: parseHoursValue(item.tuesdayHours),
              wednesdayHours: parseHoursValue(item.wednesdayHours),
              thursdayHours: parseHoursValue(item.thursdayHours),
              fridayHours: parseHoursValue(item.fridayHours),
              saturdayHours: parseHoursValue(item.saturdayHours),
              sundayHours: parseHoursValue(item.sundayHours),
              description: item.description || '',
              billable: item.billable,
            })) || []
          );
        } else {
          setTimesheet(null);
          setItems([]);
        }
      } catch (error) {
        console.error('❌ Error loading timesheet for week:', error);
        setTimesheet(null);
        setItems([]);
      }
    }
  };

  function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const navigateWeek = (direction: 'next' | 'prev') => {
    const newDate = new Date(weekStartDate);
    if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setWeekStartDate(getMonday(newDate));
  };

  const getWeekEndDate = () => {
    const endDate = new Date(weekStartDate);
    endDate.setDate(endDate.getDate() + 6);
    return endDate;
  };

  const formatWeekRange = () => {
    const endDate = getWeekEndDate();
    return `${weekStartDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  const addNewItem = () => {
    const newItem: TimesheetItemForm = {
      projectId: projects.length > 0 ? projects[0].id : '',
      taskId: '',
      taskDescription: '',
      mondayHours: 0,
      tuesdayHours: 0,
      wednesdayHours: 0,
      thursdayHours: 0,
      fridayHours: 0,
      saturdayHours: 0,
      sundayHours: 0,
      description: '',
      billable: true,
    };
    setItems([...items, newItem]);
  };

  const getIssueDisplayId = (iss: Issue): string => {
    return (iss as any).timeTrackerTaskId || (iss as any).issueKey || '';
  };

  const addIssueToTimesheet = (issue: Issue) => {
    const taskId = getIssueDisplayId(issue);
    const newItem: TimesheetItemForm = {
      projectId: (issue as any).projectId || projects[0]?.id || '',
      taskId: taskId || '',
      taskDescription: issue.title || '',
      mondayHours: 0,
      tuesdayHours: 0,
      wednesdayHours: 0,
      thursdayHours: 0,
      fridayHours: 0,
      saturdayHours: 0,
      sundayHours: 0,
      description: '',
      billable: true,
    };
    setItems((prev) => [...prev, newItem]);
  };

  type HourField =
    | 'mondayHours'
    | 'tuesdayHours'
    | 'wednesdayHours'
    | 'thursdayHours'
    | 'fridayHours'
    | 'saturdayHours'
    | 'sundayHours';

  const updateItem = (index: number, field: keyof TimesheetItemForm, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleHourChange = (index: number, field: HourField, rawValue: string) => {
    if (!canEdit) return;
    if (rawValue === '') {
      updateItem(index, field, 0);
      return;
    }
    const normalized = rawValue.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (Number.isNaN(parsed)) {
      return;
    }
    updateItem(index, field, parseHoursValue(parsed));
  };

  const serializeTimesheetItems = (list: TimesheetItemForm[]) =>
    list.map(item => ({
      projectId: item.projectId,
      taskId: item.taskId || '',
      taskDescription: item.taskDescription,
      mondayHours: parseHoursValue(item.mondayHours),
      tuesdayHours: parseHoursValue(item.tuesdayHours),
      wednesdayHours: parseHoursValue(item.wednesdayHours),
      thursdayHours: parseHoursValue(item.thursdayHours),
      fridayHours: parseHoursValue(item.fridayHours),
      saturdayHours: parseHoursValue(item.saturdayHours),
      sundayHours: parseHoursValue(item.sundayHours),
      description: item.description || '',
      billable: item.billable
    }));

  const handleProjectChange = (index: number, projectIdValue: string) => {
    updateItem(index, 'projectId', projectIdValue);
    // Clear TYVRA options for row and refetch assigned issues for new project
    setTyvraOptionsByRow((prev) => ({ ...prev, [index]: [] }));
    fetchTyvraOptions(index, projectIdValue);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const getTotalHours = () => {
    return items.reduce((total, item) => {
      return total + 
        parseHoursValue(item.mondayHours) + 
        parseHoursValue(item.tuesdayHours) + 
        parseHoursValue(item.wednesdayHours) + 
        parseHoursValue(item.thursdayHours) + 
        parseHoursValue(item.fridayHours) + 
        parseHoursValue(item.saturdayHours) + 
        parseHoursValue(item.sundayHours);
    }, 0);
  };

  const handleSave = async () => {
    // Validate that at least one entry has hours
    const hasHours = items.some((it) => 
      parseHoursValue(it.mondayHours) + 
      parseHoursValue(it.tuesdayHours) + 
      parseHoursValue(it.wednesdayHours) + 
      parseHoursValue(it.thursdayHours) + 
      parseHoursValue(it.fridayHours) + 
      parseHoursValue(it.saturdayHours) + 
      parseHoursValue(it.sundayHours) > 0
    );
    if (!hasHours) {
      setValidationError('Please enter hours for at least one day.');
      return;
    }
    setValidationError(null);
    if (items.length === 0) {
      setError('Please add at least one time entry');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const fromDate = weekStartDate.toISOString().split('T')[0];
      const toDate = getWeekEndDate().toISOString().split('T')[0];

      const payloadItems = serializeTimesheetItems(items);

      if (timesheet) {
        // Update existing timesheet
        const result = await apiService.updateTimesheet(timesheet.id, { items: payloadItems });
        console.log('✅ Timesheet updated successfully:', result);
        setTimesheet(result);
      } else {
        // Create new timesheet
        const result = await apiService.createTimesheet({
          fromDate,
          toDate,
          projectId: items[0].projectId, // Use first item's project
          items: payloadItems
        });
        console.log('✅ Timesheet created successfully:', result);
        setTimesheet({ 
          id: result.id, 
          userId: '', // Will be set by backend
          fromDate, 
          toDate, 
          status: TimesheetStatus.DRAFT,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      // Show success message
      setSuccess('Timesheet saved successfully!');
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
      // Stay on the same page instead of navigating away
      console.log('✅ Timesheet saved successfully, staying on page');
          } catch (error) {
        console.error('❌ Timesheet save failed:', error);
        setError('Failed to save timesheet');
        setSuccess(null);
      } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate that at least one entry has hours
      const hasHours = items.some((it) => 
      parseHoursValue(it.mondayHours) + 
      parseHoursValue(it.tuesdayHours) + 
      parseHoursValue(it.wednesdayHours) + 
      parseHoursValue(it.thursdayHours) + 
      parseHoursValue(it.fridayHours) + 
      parseHoursValue(it.saturdayHours) + 
      parseHoursValue(it.sundayHours) > 0
      );
      if (!hasHours) {
        setValidationError('Please enter hours for at least one day.');
        setSubmitDialog(false);
        return;
      }
      setValidationError(null);
      
      if (items.length === 0) {
        setError('Please add at least one time entry');
        setSubmitDialog(false);
        return;
      }

      let timesheetId = timesheet?.id;
      
      // If timesheet doesn't exist, create it first
      const payloadItems = serializeTimesheetItems(items);

      if (!timesheetId) {
        const fromDate = weekStartDate.toISOString().split('T')[0];
        const toDate = getWeekEndDate().toISOString().split('T')[0];
        
        const result = await apiService.createTimesheet({
          fromDate,
          toDate,
          projectId: items[0].projectId,
          items: payloadItems
        });
        timesheetId = result.id;
        setTimesheet({ 
          id: result.id, 
          userId: '', 
          fromDate, 
          toDate, 
          status: TimesheetStatus.DRAFT,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      } else if (items.length > 0) {
        // If timesheet exists and has modifications, save them first
        await apiService.updateTimesheet(timesheetId, { items: payloadItems });
      }
      
      // Then submit the timesheet
      await apiService.submitTimesheet(timesheetId);
      setSuccess('Timesheet submitted successfully!');
      setSubmitDialog(false);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to submit timesheet:', error);
      setError('Failed to submit timesheet');
      setSubmitDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async () => {
    if (!timesheet) return;

    try {
      setLoading(true);
      if (approvalType === 'approve') {
        await apiService.approveTimesheet(timesheet.id, comments);
        setSuccess('Timesheet approved successfully!');
      } else {
        await apiService.rejectTimesheet(timesheet.id, reason, comments);
        setSuccess('Timesheet rejected successfully!');
      }

      // Clear form
      setComments('');
      setReason('');
      setApprovalDialog(false);
      setRejectionDialog(false);

      // Reload timesheet data
      loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to process timesheet:', error);
      setError('Failed to process timesheet');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalDialog = () => {
    setApprovalType('approve');
    setComments('');
    setApprovalDialog(true);
  };

  const openRejectionDialog = () => {
    setApprovalType('reject');
    setComments('');
    setReason('');
    setRejectionDialog(true);
  };

  const canEdit = !timesheet || 
    (timesheet.status === TimesheetStatus.DRAFT || timesheet.status === TimesheetStatus.REJECTED) && 
    timesheet.userId === user?.id;
  const canSubmit = (!timesheet || 
    (timesheet.status === TimesheetStatus.DRAFT || timesheet.status === TimesheetStatus.REJECTED)) && 
    (!timesheet || timesheet.userId === user?.id);

  if (loading && !timesheet) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/timesheets')}
            sx={{ borderRadius: 2 }}
          >
            Back to Timesheets
          </Button>
          <Typography variant="h4">
            {id ? 'Edit Timesheet' : 'New Timesheet'}
          </Typography>
        </Box>
      </Box>
        {timesheet && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={timesheet.status}
              color={
                timesheet.status === TimesheetStatus.APPROVED ? 'success' :
                timesheet.status === TimesheetStatus.REJECTED ? 'error' :
                timesheet.status === TimesheetStatus.SUBMITTED ? 'warning' :
                'default'
              }
              size="medium"
            />
            {timesheet.status === TimesheetStatus.REJECTED && timesheet.rejectionReason && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="error.dark" gutterBottom>
                  Rejection Details:
                </Typography>
                <Typography variant="body2" color="error.dark" gutterBottom>
                  <strong>Reason:</strong> {timesheet.rejectionReason}
                </Typography>
                {timesheet.rejectionComments && (
                  <Typography variant="body2" color="error.dark">
                    <strong>Comments:</strong> {timesheet.rejectionComments}
                  </Typography>
                )}
                {timesheet.rejectedAt && (
                  <Typography variant="caption" color="error.dark" sx={{ display: 'block', mt: 1 }}>
                    Rejected on: {new Date(timesheet.rejectedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Week Navigation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigateWeek('prev')}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h6">{formatWeekRange()}</Typography>
              <IconButton onClick={() => navigateWeek('next')}>
                <ChevronRight />
              </IconButton>
            </Box>
            <Button
              variant="outlined"
              startIcon={<CalendarToday />}
              onClick={() => setCalendarOpen(true)}
            >
              Choose Date
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Assigned Issues Suggestions */}
      {assignedIssueSuggestions && assignedIssueSuggestions.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Assigned to you (quick add)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {assignedIssueSuggestions.slice(0, 16).map((iss) => {
                const id = getIssueDisplayId(iss);
                const title = iss.title || '';
                return (
                  <Tooltip key={(iss as any).id} title={title} arrow>
                    <Chip
                      label={id}
                      onClick={() => addIssueToTimesheet(iss)}
                      icon={<Add fontSize="small" />}
                      sx={{
                        maxWidth: 200,
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }
                      }}
                      clickable
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Calendar Dialog */}
      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)}>
        <DialogTitle>Select Week Start Date</DialogTitle>
        <DialogContent>
          <DatePicker
            label="Week Starting"
            value={weekStartDate}
            onChange={(date) => {
              if (date) {
                setWeekStartDate(getMonday(date));
                setCalendarOpen(false);
              }
            }}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Time Entries</Typography>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addNewItem}
              sx={{
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Add Entry
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>TYVRA ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Task Description</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Mon</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Tue</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Wed</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Thu</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Fri</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Sat</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', width: '90px', minWidth: '90px' }}>Sun</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Billable</TableCell>
                {canEdit && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={item.projectId}
                      onChange={(e) => handleProjectChange(index, e.target.value)}
                      disabled={!canEdit}
                      required
                      sx={{ minWidth: 150 }}
                    >
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      disabled={!canEdit}
                      options={tyvraOptionsByRow[index] || []}
                      getOptionLabel={(opt: any) => (typeof opt === 'string' ? opt : (opt.label || opt.value || ''))}
                      isOptionEqualToValue={(opt: any, val: any) => (opt?.value || opt) === (val?.value || val)}
                      sx={{ width: '100%' }}
                      loading={!!optionsLoadingByRow[index]}
                      loadingText="Loading..."
                      noOptionsText={tyvraInputByRow[index] ? 'No matches' : 'Start typing to search'}
                      openOnFocus
                      inputValue={tyvraInputByRow[index] ?? item.taskId ?? ''}
                      onOpen={() => {
                        if (!tyvraOptionsByRow[index] || tyvraOptionsByRow[index].length === 0) {
                          fetchTyvraOptions(index, item.projectId);
                        }
                      }}
                      onInputChange={async (_e, value) => {
                        // Update field immediately for free typing
                        updateItem(index, 'taskId', value);
                        setTyvraInputByRow((prev) => ({ ...prev, [index]: value }));
                        // Debounce fetching to avoid race conditions
                        if (tyvraDebounceRef.current[index]) {
                          clearTimeout(tyvraDebounceRef.current[index]);
                        }
                        tyvraDebounceRef.current[index] = setTimeout(() => {
                          if (item.projectId) {
                            if (value && value.trim() !== '') {
                              fetchTyvraOptions(index, item.projectId, value);
                            } else {
                              fetchTyvraOptions(index, item.projectId);
                            }
                          }
                        }, 250);
                      }}
                      onChange={(_e, value) => {
                        if (typeof value === 'string') {
                          updateItem(index, 'taskId', value);
                          setTyvraInputByRow((prev) => ({ ...prev, [index]: value }));
                        } else if (value && (value as any).value) {
                          updateItem(index, 'taskId', (value as any).value);
                          setTyvraInputByRow((prev) => ({ ...prev, [index]: (value as any).value }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select TYVRA ID (Optional)"
                          variant="outlined"
                          size="small"
                          inputProps={{
                            ...params.inputProps,
                            size: 10
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => {
                        const showId = typeof option === 'string' ? option : (option.id || option.value || '');
                        const showTitle = typeof option === 'string' ? '' : (option.title || '');
                        return (
                          <Tooltip
                            title={showTitle ? `${showId} - ${showTitle}` : showId}
                            arrow
                            placement="right"
                            enterDelay={300}
                          >
                            <li {...props} style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 6, paddingBottom: 6 }}>
                              <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {showId}
                              </span>
                              {showTitle && (
                                <span style={{ fontSize: 12, lineHeight: 1.2, color: 'rgba(0,0,0,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                                  {showTitle}
                                </span>
                              )}
                            </li>
                          </Tooltip>
                        );
                      }}
                      filterOptions={(x) => x}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.taskDescription}
                      onChange={(e) => updateItem(index, 'taskDescription', e.target.value)}
                      placeholder="Task description"
                      disabled={!canEdit}
                      required
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.mondayHours}
                      onChange={(e) => handleHourChange(index, 'mondayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.tuesdayHours}
                      onChange={(e) => handleHourChange(index, 'tuesdayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.wednesdayHours}
                      onChange={(e) => handleHourChange(index, 'wednesdayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.thursdayHours}
                      onChange={(e) => handleHourChange(index, 'thursdayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.fridayHours}
                      onChange={(e) => handleHourChange(index, 'fridayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.saturdayHours}
                      onChange={(e) => handleHourChange(index, 'saturdayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '90px', minWidth: '90px' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={item.sundayHours}
                      onChange={(e) => handleHourChange(index, 'sundayHours', e.target.value)}
                      inputProps={{ min: 0, max: 24, step: 0.25 }}
                      disabled={!canEdit}
                      sx={{ 
                        width: '100%',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '8px 6px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Additional notes"
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={item.billable}
                      onChange={(e) => updateItem(index, 'billable', e.target.value === 'true')}
                      disabled={!canEdit}
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <IconButton onClick={() => removeItem(index)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Total Hours: {getTotalHours().toFixed(2)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/timesheets')}
            >
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={loading}
              >
                Save
              </Button>
            )}
            {canSubmit && items.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Send />}
                onClick={() => setSubmitDialog(true)}
                disabled={loading}
              >
                Submit for Approval
              </Button>
            )}
            {/* Manager approval buttons */}
            {user?.role === 'manager' && timesheet?.status === TimesheetStatus.SUBMITTED && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ThumbUp />}
                  onClick={openApprovalDialog}
                  disabled={loading}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ThumbDown />}
                  onClick={openRejectionDialog}
                  disabled={loading}
                >
                  Reject
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog open={submitDialog} onClose={() => setSubmitDialog(false)}>
        <DialogTitle>Submit Timesheet</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit this timesheet for approval?
            Once submitted, you won't be able to edit it unless it's rejected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Timesheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to approve this timesheet?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Timesheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this timesheet.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for rejection"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                placeholder="e.g., Incorrect hours, missing details..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            disabled={!reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TimesheetForm;